import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxList,
  ComboboxItem,
} from "@/components/ui/combobox";
import { useTaskStats } from "@/lib/api/tasks/hooks";
import { useClasses } from "@/lib/api/classes/hooks";
import { ClipboardList, CheckSquare, Brain, TrendingUp } from "lucide-react";

function TaskStatsPage() {
  const [selectedClassId, setSelectedClassId] = useState<string>("");

  const { data: classesData } = useClasses();
  const { data: stats, isLoading } = useTaskStats({
    classId: selectedClassId || undefined,
  });

  return (
    <PageContainer>
      <Header title="태스크 통계" />

      <div className="flex flex-col gap-6">
        {/* 필터 */}
        <div className="flex flex-wrap gap-3">
          <Combobox
            items={classesData ?? []}
            value={classesData?.find((cls) => cls._id === selectedClassId) ?? null}
            onValueChange={(cls) => {
              setSelectedClassId(cls?._id ?? "");
            }}
            itemToStringLabel={(cls) => cls.name}
            isItemEqualToValue={(a, b) => a._id === b._id}
          >
            <ComboboxInput placeholder="전체 반" showClear className="w-[180px]" />
            <ComboboxContent>
              <ComboboxEmpty>결과 없음</ComboboxEmpty>
              <ComboboxList>
                {(cls) => (
                  <ComboboxItem key={cls._id} value={cls}>
                    {cls.name}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </div>

        {/* 통계 카드 */}
        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">
            로딩 중...
          </div>
        ) : !stats ? (
          <div className="p-8 text-center text-muted-foreground">
            데이터가 없습니다.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  전체 태스크
                </CardTitle>
                <ClipboardList className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalTasks}</div>
                <p className="text-xs text-muted-foreground">
                  일반 {stats.generalTasks} / 퀴즈 {stats.quizTasks}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  일반 태스크
                </CardTitle>
                <CheckSquare className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.generalTasks}</div>
                <p className="text-xs text-muted-foreground">총 개수</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  퀴즈 태스크
                </CardTitle>
                <Brain className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.quizTasks}</div>
                <p className="text-xs text-muted-foreground">총 개수</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">완료율</CardTitle>
                <TrendingUp className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.completionRate}%
                </div>
                <p className="text-xs text-muted-foreground">
                  일반 태스크 기준
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* 완료율 그래프 */}
        {stats && stats.completionRate > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">일반 태스크 완료율</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>완료</span>
                  <span>{stats.completionRate}%</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${stats.completionRate}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 안내 메시지 */}
        <Card>
          <CardContent className="py-6">
            <p className="text-sm text-muted-foreground text-center">
              더 상세한 통계 기능이 곧 추가될 예정입니다.
            </p>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/tasks/stats")({
  component: TaskStatsPage,
});
